(() => {
  if (customElements.get('uvfeel-personalization')) return;

  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const EXTENSION_MIME_TYPES = {
    png: ['image/png'],
    jpg: ['image/jpeg', 'image/jpg', 'image/pjpeg'],
    jpeg: ['image/jpeg', 'image/jpg', 'image/pjpeg'],
    svg: ['image/svg+xml'],
    pdf: ['application/pdf', 'application/x-pdf'],
  };
  const ALLOWED_EXTENSIONS = new Set(Object.keys(EXTENSION_MIME_TYPES));
  const BITMAP_EXTENSIONS = new Set(['png', 'jpg', 'jpeg']);
  const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
  const SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;

  const stateStore = window.UVFeelPersonalizationState instanceof Map
    ? window.UVFeelPersonalizationState
    : new Map();
  window.UVFeelPersonalizationState = stateStore;

  const getExtension = (name = '') => {
    const parts = name.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  };

  const isAllowedFile = (file) => {
    if (!file || typeof file.name !== 'string') return false;
    const extension = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.has(extension)) return false;
    const type = (file.type || '').trim().toLowerCase();
    return !type || EXTENSION_MIME_TYPES[extension].includes(type);
  };

  const isBitmapFile = (file) => {
    if (!file) return false;
    const extension = getExtension(file.name);
    const type = (file.type || '').trim().toLowerCase();
    return BITMAP_EXTENSIONS.has(extension) && (!type || ['image/png', 'image/jpeg', 'image/jpg', 'image/pjpeg'].includes(type));
  };

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 1024) return `${bytes || 0} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const normalizeUrl = (value, messages) => {
    const input = (value || '').trim();
    if (!input) return { valid: false, value: '', error: messages.required };
    if (CONTROL_CHARACTERS.test(input)) return { valid: false, value: input, error: messages.invalid };

    let candidate = input;
    if (candidate.startsWith('//')) candidate = `https:${candidate}`;
    else if (!SCHEME_PATTERN.test(candidate)) candidate = `https://${candidate}`;

    let parsed;
    try {
      parsed = new URL(candidate);
    } catch {
      return { valid: false, value: input, error: messages.invalid };
    }

    if (!['http:', 'https:'].includes(parsed.protocol)) return { valid: false, value: input, error: messages.protocol };
    if (parsed.username || parsed.password) return { valid: false, value: input, error: messages.credentials };
    if (!parsed.hostname || parsed.hostname === '.' || !/[a-z\d]/i.test(parsed.hostname)) {
      return { valid: false, value: input, error: messages.invalid };
    }

    return { valid: true, value: parsed.toString(), error: '' };
  };

  class UVFeelPersonalization extends HTMLElement {
    constructor() {
      super();
      this.previewObjectUrl = null;
      this.previewToken = 0;
      this.unsubscribeVariantSelection = null;
      this.form = null;
      this.stateKey = '';
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onFormInvalid = this.onFormInvalid.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onFileChange = this.onFileChange.bind(this);
      this.onFileChangeClick = this.onFileChangeClick.bind(this);
      this.onFileRemoveClick = this.onFileRemoveClick.bind(this);
      this.onUrlBlur = this.onUrlBlur.bind(this);
      this.onUrlInput = this.onUrlInput.bind(this);
      this.onTextInput = this.onTextInput.bind(this);
      this.onWifiInput = this.onWifiInput.bind(this);
      this.onHiddenInput = this.onHiddenInput.bind(this);
      this.onChoiceChange = this.onChoiceChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      if (this.form) return;

      this.form = this.closest('form');
      if (!this.form) return;

      this.stateKey = this.dataset.stateKey || this.dataset.productFormId || this.id;
      this.personalizationType = this.dataset.personalizationType || 'legacy';
      this.fileInput = this.querySelector('[data-file-input]');
      this.filePicker = this.querySelector('[data-file-picker]');
      this.fileSelected = this.querySelector('[data-file-selected]');
      this.filePreview = this.querySelector('[data-file-preview]');
      this.fileDocumentIcon = this.querySelector('[data-file-document-icon]');
      this.fileName = this.querySelector('[data-file-name]');
      this.fileMeta = this.querySelector('[data-file-meta]');
      this.fileError = this.querySelector('[data-file-error]');
      this.fileChange = this.querySelector('[data-file-change]');
      this.fileRemove = this.querySelector('[data-file-remove]');
      this.urlInputs = Array.from(this.querySelectorAll('[data-url-input]'));
      this.textInputs = Array.from(this.querySelectorAll('[data-text-input]'));
      this.textareaInputs = Array.from(this.querySelectorAll('[data-textarea-input]'));
      this.personalizationControls = Array.from(this.querySelectorAll('[data-personalization-control]'));
      this.choiceInputs = Array.from(this.querySelectorAll('[data-choice-input]'));
      this.personalizationFields = this.querySelector('[data-personalization-fields]');
      this.laterInfo = this.querySelector('[data-later-info]');
      this.nowInfo = this.querySelector('[data-now-info]');
      this.laterProperty = this.querySelector('[data-later-property]');
      this.hiddenInput = this.querySelector('[data-wifi-hidden]');
      this.hiddenFallback = this.querySelector('[data-wifi-hidden-fallback]');
      this.ssidInput = this.querySelector('[data-ssid-input]');
      this.encryptionInput = this.querySelector('[data-wifi-encryption]');
      this.passwordInput = this.querySelector('[data-wifi-password]');
      this.passwordField = this.querySelector('[data-wifi-password-field]');
      this.summary = this.querySelector('[data-summary]');
      this.summaryFile = this.querySelector('[data-summary-file]');
      this.summaryFileName = this.querySelector('[data-summary-file-name]');
      this.summaryUrl = this.querySelector('[data-summary-url]');
      this.summaryUrlLink = this.querySelector('[data-summary-url-link]');
      this.summaryUrlValue = this.querySelector('[data-summary-url-value]');
      this.summaryNotes = this.querySelector('[data-summary-notes]');
      this.summaryNotesValue = this.querySelector('[data-summary-notes-value]');
      this.summaryText = this.querySelector('[data-summary-custom="text"]');
      this.summaryTextValue = this.querySelector('[data-summary-text-value]');
      this.summaryCustomLink = this.querySelector('[data-summary-custom="link"]');
      this.summaryCustomLinkAnchor = this.querySelector('[data-summary-custom-link]');
      this.summaryCustomLinkValue = this.querySelector('[data-summary-link-value]');
      this.summaryWifi = this.querySelector('[data-summary-custom="wifi"]');
      this.summaryWifiValue = this.querySelector('[data-summary-wifi-value]');

      this.personalizationControls.forEach((control) => {
        control.dataset.requiredNow = control.required ? 'true' : 'false';
      });
      this.fileInput?.addEventListener('change', this.onFileChange);
      this.fileChange?.addEventListener('click', this.onFileChangeClick);
      this.fileRemove?.addEventListener('click', this.onFileRemoveClick);
      this.urlInputs.forEach((input) => {
        input.addEventListener('blur', this.onUrlBlur);
        input.addEventListener('input', this.onUrlInput);
      });
      this.textInputs.forEach((input) => input.addEventListener('input', this.onTextInput));
      this.textareaInputs.forEach((input) => input.addEventListener('input', this.onTextInput));
      this.encryptionInput?.addEventListener('change', this.onWifiInput);
      this.ssidInput?.addEventListener('input', this.onWifiInput);
      this.passwordInput?.addEventListener('input', this.onWifiInput);
      this.hiddenInput?.addEventListener('change', this.onHiddenInput);
      this.choiceInputs.forEach((input) => input.addEventListener('change', this.onChoiceChange));
      this.form.addEventListener('submit', this.onFormSubmit, true);
      this.form.addEventListener('invalid', this.onFormInvalid, true);
      document.addEventListener('change', this.onVariantInputChange, true);
      window.addEventListener('pageshow', this.onPageShow);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantSelection = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          ({ data }) => {
            const productInfo = this.closest('product-info');
            const eventProductInfo = data?.event?.target?.closest?.('product-info');
            if (productInfo && eventProductInfo === productInfo) this.saveState();
          }
        );
      }

      this.restoreState();
      this.setChoice(this.getChoice(), false);
      this.updateSummary();
    }

    disconnectedCallback() {
      this.fileInput?.removeEventListener('change', this.onFileChange);
      this.fileChange?.removeEventListener('click', this.onFileChangeClick);
      this.fileRemove?.removeEventListener('click', this.onFileRemoveClick);
      this.urlInputs?.forEach((input) => {
        input.removeEventListener('blur', this.onUrlBlur);
        input.removeEventListener('input', this.onUrlInput);
      });
      this.textInputs?.forEach((input) => input.removeEventListener('input', this.onTextInput));
      this.textareaInputs?.forEach((input) => input.removeEventListener('input', this.onTextInput));
      this.encryptionInput?.removeEventListener('change', this.onWifiInput);
      this.ssidInput?.removeEventListener('input', this.onWifiInput);
      this.passwordInput?.removeEventListener('input', this.onWifiInput);
      this.hiddenInput?.removeEventListener('change', this.onHiddenInput);
      this.choiceInputs?.forEach((input) => input.removeEventListener('change', this.onChoiceChange));
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.form?.removeEventListener('invalid', this.onFormInvalid, true);
      document.removeEventListener('change', this.onVariantInputChange, true);
      window.removeEventListener('pageshow', this.onPageShow);
      this.unsubscribeVariantSelection?.();
      this.unsubscribeVariantSelection = null;
      this.form = null;
      this.revokePreviewObjectUrl();

      const stateKey = this.stateKey;
      setTimeout(() => {
        if (!stateKey) return;
        const stateStillMounted = Array.from(document.querySelectorAll('[data-uvfeel-personalization]'))
          .some((element) => element.dataset.stateKey === stateKey);
        if (!stateStillMounted) stateStore.delete(stateKey);
      }, 0);
    }

    get messages() {
      return {
        fileRequired: this.dataset.fileRequired || 'Add your company logo.',
        fileType: this.dataset.fileType || 'Use PNG, JPG, SVG or PDF.',
        fileSize: this.dataset.fileSize || 'This file is larger than 10 MB.',
        fileRead: this.dataset.fileRead || 'We could not read this file. Choose it again.',
        textRequired: this.dataset.textRequired || 'Enter the text.',
        textMax: this.dataset.textMax || 'Use no more than 12 characters.',
        ssidRequired: this.dataset.ssidRequired || 'Enter the Wi-Fi network name.',
        passwordRequired: this.dataset.passwordRequired || 'Enter the Wi-Fi password.',
        encryptionRequired: this.dataset.encryptionRequired || 'Choose an encryption type.',
        url: {
          required: this.dataset.urlRequired || 'Add the destination link.',
          protocol: this.dataset.urlProtocol || 'Use an HTTP(S) link.',
          credentials: this.dataset.urlCredentials || 'Remove the username or password from this link.',
          invalid: this.dataset.urlInvalid || 'Enter a valid destination link.',
        },
      };
    }

    get maxFileSize() {
      const configured = Number.parseInt(this.dataset.maxFileSize, 10);
      return Number.isFinite(configured) && configured > 0 ? configured : MAX_FILE_SIZE;
    }

    onVariantInputChange(event) {
      if (!event.target.closest?.('variant-selects')) return;
      const productInfo = this.closest('product-info');
      if (productInfo && event.target.closest('product-info') === productInfo) this.saveState();
    }

    onFileChange() {
      this.setFileState(this.fileInput?.files?.[0] || null, true);
    }

    onFileChangeClick(event) {
      event.preventDefault();
      this.fileInput?.click();
    }

    onFileRemoveClick(event) {
      event.preventDefault();
      if (this.fileInput) this.fileInput.value = '';
      this.setFileState(null, true);
      this.fileInput?.focus();
    }

    onUrlBlur(event) {
      this.validateUrl(event.currentTarget, true);
      this.saveState();
      this.updateSummary();
    }

    onUrlInput(event) {
      this.setControlError(event.currentTarget, 'url', '');
      event.currentTarget.setCustomValidity('');
      this.saveState();
      this.updateSummary();
    }

    onTextInput() {
      this.textInputs.forEach((input) => this.validateText(input, false));
      this.saveState();
      this.updateSummary();
    }

    onWifiInput() {
      this.updateWifiPasswordState();
      this.validateWifi(false);
      this.saveState();
      this.updateSummary();
    }

    onHiddenInput() {
      this.syncHiddenControls();
      this.saveState();
      this.updateSummary();
    }

    onChoiceChange(event) {
      this.setChoice(event.target.value, true);
    }

    onPageShow() {
      this.setChoice(this.getChoice(), true);
    }

    getChoice() {
      return this.choiceInputs?.find((input) => input.checked)?.value === 'later' ? 'later' : 'now';
    }

    isLaterChoice() {
      return this.getChoice() === 'later';
    }

    setChoice(choice, shouldSave = true) {
      const isLater = choice === 'later';
      this.choiceInputs?.forEach((input) => {
        input.checked = isLater ? input.dataset.choiceInput === 'later' : input.dataset.choiceInput === 'now';
      });
      this.personalizationFields?.toggleAttribute('hidden', isLater);
      this.laterInfo?.toggleAttribute('hidden', !isLater);
      this.nowInfo?.toggleAttribute('hidden', isLater);
      this.laterProperty?.toggleAttribute('disabled', !isLater);
      this.personalizationControls?.forEach((control) => {
        control.disabled = isLater;
        control.required = !isLater && control.dataset.requiredNow === 'true';
      });
      this.syncHiddenControls();
      this.updateWifiPasswordState();

      if (isLater) {
        this.fileInput?.setCustomValidity('');
        this.urlInputs?.forEach((input) => input.setCustomValidity(''));
        this.textInputs?.forEach((input) => input.setCustomValidity(''));
        this.ssidInput?.setCustomValidity('');
        this.passwordInput?.setCustomValidity('');
        this.encryptionInput?.setCustomValidity('');
        this.clearAllErrors();
      }

      if (shouldSave) this.saveState();
      this.updateSummary();
    }

    syncHiddenControls() {
      if (!this.hiddenInput || !this.hiddenFallback) return;
      this.hiddenFallback.disabled = this.isLaterChoice() || this.hiddenInput.checked;
    }

    updateWifiPasswordState() {
      if (!this.passwordInput || !this.passwordField || !this.encryptionInput) return;
      const encrypted = this.encryptionInput.value !== '' && this.encryptionInput.value !== 'None';
      const disabled = this.isLaterChoice() || !encrypted;
      this.passwordField.toggleAttribute('hidden', disabled);
      this.passwordInput.disabled = disabled;
      this.passwordInput.required = !disabled;
      if (disabled) {
        this.passwordInput.setCustomValidity('');
        this.setControlError(this.passwordInput, 'password', '');
      }
    }

    onFormInvalid(event) {
      if (this.isLaterChoice()) return;
      if (event.target === this.fileInput) this.validateFile(true);
      else if (this.urlInputs.includes(event.target)) this.validateUrl(event.target, true);
      else if (this.textInputs.includes(event.target)) this.validateText(event.target, true);
      else if (event.target === this.ssidInput || event.target === this.passwordInput || event.target === this.encryptionInput) this.validateWifi(true);
    }

    onFormSubmit(event) {
      if (this.isLaterChoice()) {
        this.clearAllCustomValidity();
        this.saveState();
        return;
      }

      const valid = this.validateAll(true);
      this.saveState();
      this.updateSummary();
      if (valid) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const firstInvalid = this.personalizationControls.find((control) => !control.disabled && !control.checkValidity());
      firstInvalid?.focus({ preventScroll: true });
      this.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }

    validateAll(showError = false) {
      let valid = true;
      if (this.fileInput) valid = this.validateFile(showError) && valid;
      this.urlInputs.forEach((input) => { valid = this.validateUrl(input, showError) && valid; });
      this.textInputs.forEach((input) => { valid = this.validateText(input, showError) && valid; });
      if (this.personalizationType === 'wifi') valid = this.validateWifi(showError) && valid;
      return valid;
    }

    validateFile(showError = false) {
      if (!this.fileInput) return true;
      if (this.isLaterChoice()) {
        this.fileInput.setCustomValidity('');
        if (showError) this.setFileError('');
        return true;
      }
      const file = this.fileInput.files?.[0] || null;
      let error = '';
      if (!file) error = this.messages.fileRequired;
      else if (!isAllowedFile(file)) error = this.messages.fileType;
      else if (file.size > this.maxFileSize) error = this.messages.fileSize;
      this.fileInput.setCustomValidity(error);
      if (showError) this.setFileError(error);
      else if (!error) this.setFileError('');
      return !error;
    }

    validateUrl(input, showError = false) {
      if (!input) return true;
      if (this.isLaterChoice()) {
        input.setCustomValidity('');
        if (showError) this.setControlError(input, 'url', '');
        return true;
      }
      const value = input.value.trim();
      if (!value && !input.required) {
        input.setCustomValidity('');
        this.setControlError(input, 'url', '');
        return true;
      }
      const result = normalizeUrl(value, this.messages.url);
      if (result.valid) {
        input.value = result.value;
        input.setCustomValidity('');
        this.setControlError(input, 'url', '');
        const checkLink = input.closest('.uvfeel-personalization__field')?.querySelector('[data-url-check]');
        checkLink?.toggleAttribute('hidden', false);
        if (checkLink) checkLink.href = result.value;
      } else {
        input.setCustomValidity(result.error);
        if (showError) this.setControlError(input, 'url', result.error);
        const checkLink = input.closest('.uvfeel-personalization__field')?.querySelector('[data-url-check]');
        checkLink?.toggleAttribute('hidden', true);
        checkLink?.removeAttribute('href');
      }
      return result.valid;
    }

    validateText(input, showError = false) {
      if (!input || this.isLaterChoice()) {
        input?.setCustomValidity('');
        return true;
      }
      const value = input.value.trim();
      const maxLength = Number.parseInt(input.maxLength, 10) || 12;
      let error = '';
      if (!value && input.required) error = this.messages.textRequired;
      else if ([...input.value].length > maxLength) error = this.messages.textMax;
      input.setCustomValidity(error);
      if (showError) this.setControlError(input, 'text', error);
      else if (!error) this.setControlError(input, 'text', '');
      return !error;
    }

    validateWifi(showError = false) {
      if (this.isLaterChoice()) return true;
      let valid = true;
      if (this.ssidInput) {
        const error = this.ssidInput.value.trim() ? '' : this.messages.ssidRequired;
        this.ssidInput.setCustomValidity(error);
        this.setControlError(this.ssidInput, 'ssid', showError ? error : '');
        valid = !error && valid;
      }
      if (this.encryptionInput) {
        const error = ['None', 'WEP', 'WPA1/2/3'].includes(this.encryptionInput.value) ? '' : this.messages.encryptionRequired;
        this.encryptionInput.setCustomValidity(error);
        this.setControlError(this.encryptionInput, 'encryption', showError ? error : '');
        valid = !error && valid;
      }
      if (this.passwordInput && this.encryptionInput?.value !== 'None') {
        const error = this.passwordInput.value ? '' : this.messages.passwordRequired;
        this.passwordInput.setCustomValidity(error);
        this.setControlError(this.passwordInput, 'password', showError ? error : '');
        valid = !error && valid;
      }
      return valid;
    }

    setControlError(control, kind, message) {
      const error = control?.closest('.uvfeel-personalization__field')?.querySelector(`[data-${kind}-error]`);
      if (!error) return;
      error.textContent = message;
      error.toggleAttribute('hidden', !message);
      control?.toggleAttribute('aria-invalid', Boolean(message));
    }

    setFileError(message) {
      if (!this.fileError) return;
      this.fileError.textContent = message;
      this.fileError.toggleAttribute('hidden', !message);
      this.fileInput?.toggleAttribute('aria-invalid', Boolean(message));
    }

    clearAllErrors() {
      this.setFileError('');
      this.urlInputs?.forEach((input) => this.setControlError(input, 'url', ''));
      this.textInputs?.forEach((input) => this.setControlError(input, 'text', ''));
      if (this.ssidInput) this.setControlError(this.ssidInput, 'ssid', '');
      if (this.encryptionInput) this.setControlError(this.encryptionInput, 'encryption', '');
      if (this.passwordInput) this.setControlError(this.passwordInput, 'password', '');
    }

    clearAllCustomValidity() {
      this.personalizationControls?.forEach((control) => control.setCustomValidity(''));
    }

    setFileState(file, showError = false) {
      this.revokePreviewObjectUrl();
      this.fileInput?.setCustomValidity('');
      if (!file) {
        this.fileSelected?.toggleAttribute('hidden', true);
        this.filePicker?.toggleAttribute('hidden', false);
        if (this.fileName) this.fileName.textContent = '';
        if (this.fileMeta) this.fileMeta.textContent = '';
        this.setFileError('');
        this.saveState(false);
        this.updateSummary();
        return;
      }

      this.fileSelected?.toggleAttribute('hidden', false);
      this.filePicker?.toggleAttribute('hidden', true);
      if (this.fileName) this.fileName.textContent = file.name;
      if (this.fileMeta) this.fileMeta.textContent = `${getExtension(file.name).toUpperCase()} · ${formatFileSize(file.size)}`;
      if (this.fileDocumentIcon) this.fileDocumentIcon.textContent = getExtension(file.name).toUpperCase() || 'FILE';
      this.fileDocumentIcon?.toggleAttribute('hidden', isBitmapFile(file));
      this.filePreview?.toggleAttribute('hidden', !isBitmapFile(file));

      if (isBitmapFile(file) && this.filePreview) {
        const token = ++this.previewToken;
        const objectUrl = URL.createObjectURL(file);
        this.previewObjectUrl = objectUrl;
        this.filePreview.onload = () => {
          if (token === this.previewToken) this.filePreview.hidden = false;
        };
        this.filePreview.onerror = () => {
          if (token !== this.previewToken) return;
          this.filePreview.hidden = true;
          this.fileDocumentIcon?.toggleAttribute('hidden', false);
          this.setFileError(this.messages.fileRead);
        };
        this.filePreview.src = objectUrl;
      }

      const valid = this.validateFile(showError);
      if (showError && valid) this.setFileError('');
      this.saveState();
      this.updateSummary();
    }

    restoreState() {
      const state = stateStore.get(this.stateKey);
      if (!state) return;

      this.urlInputs.forEach((input, index) => {
        const stored = state.urls?.[input.name] ?? (index === 0 ? state.url : undefined);
        if (typeof stored === 'string') input.value = stored;
      });
      this.textInputs.forEach((input) => {
        if (typeof state.texts?.[input.name] === 'string') input.value = state.texts[input.name];
      });
      this.textareaInputs.forEach((input) => {
        if (typeof state.texts?.[input.name] === 'string') input.value = state.texts[input.name];
        else if (input.name === 'properties[Uwagi do projektu]' && typeof state.notes === 'string') input.value = state.notes;
      });
      if (this.ssidInput && typeof state.ssid === 'string') this.ssidInput.value = state.ssid;
      if (this.encryptionInput && typeof state.encryption === 'string') this.encryptionInput.value = state.encryption;
      if (this.passwordInput && typeof state.password === 'string') this.passwordInput.value = state.password;
      if (this.hiddenInput) this.hiddenInput.checked = state.hidden === true;
      if (state.file && this.fileInput && this.restoreFile(state.file)) this.setFileState(state.file, false);
      this.choiceInputs.forEach((input) => { input.checked = input.dataset.choiceInput === (state.choice === 'later' ? 'later' : 'now'); });
    }

    restoreFile(file) {
      if (!this.fileInput || typeof DataTransfer === 'undefined') return false;
      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.fileInput.files = transfer.files;
        return this.fileInput.files?.length === 1;
      } catch {
        return false;
      }
    }

    saveState(preserveFile = true) {
      if (!this.stateKey) return;
      const urls = {};
      const texts = {};
      this.urlInputs?.forEach((input) => { urls[input.name] = input.value; });
      this.textInputs?.forEach((input) => { texts[input.name] = input.value; });
      this.textareaInputs?.forEach((input) => { texts[input.name] = input.value; });
      const file = this.fileInput?.files?.[0] || (preserveFile ? stateStore.get(this.stateKey)?.file || null : null);
      stateStore.set(this.stateKey, {
        file,
        urls,
        texts,
        url: this.urlInputs?.[0]?.value || '',
        notes: this.querySelector('[data-notes-input]')?.value || '',
        ssid: this.ssidInput?.value || '',
        hidden: Boolean(this.hiddenInput?.checked),
        encryption: this.encryptionInput?.value || 'None',
        password: this.passwordInput?.value || '',
        choice: this.getChoice(),
      });
    }

    updateSummary() {
      if (!this.summary) return;
      const later = this.isLaterChoice();
      const file = this.fileInput?.files?.[0] || null;
      const validUrls = this.urlInputs.map((input) => normalizeUrl(input.value, this.messages.url)).filter((result) => result.valid);
      const notes = this.querySelector('[data-notes-input]')?.value.trim() || '';
      const text = this.textInputs?.[0]?.value.trim() || '';
      const hasSummary = !later && Boolean(file || validUrls.length || notes || text || this.ssidInput?.value.trim());
      this.summary.toggleAttribute('hidden', !hasSummary);
      this.summaryFile?.toggleAttribute('hidden', !file);
      this.summaryUrl?.toggleAttribute('hidden', !(this.personalizationType === 'legacy' && validUrls[0]));
      this.summaryNotes?.toggleAttribute('hidden', !notes);
      this.summaryText?.toggleAttribute('hidden', !text || this.personalizationType === 'legacy');
      this.summaryCustomLink?.toggleAttribute('hidden', !validUrls[0] || this.personalizationType === 'legacy');
      this.summaryWifi?.toggleAttribute('hidden', this.personalizationType !== 'wifi' || !this.ssidInput?.value.trim());
      if (this.summaryFileName) this.summaryFileName.textContent = file?.name || '';
      if (this.summaryUrlValue) this.summaryUrlValue.textContent = validUrls[0]?.value || '';
      if (this.summaryUrlLink) {
        if (validUrls[0]) this.summaryUrlLink.href = validUrls[0].value;
        else this.summaryUrlLink.removeAttribute('href');
      }
      if (this.summaryNotesValue) this.summaryNotesValue.textContent = notes;
      if (this.summaryTextValue) this.summaryTextValue.textContent = text;
      if (this.summaryCustomLinkValue) this.summaryCustomLinkValue.textContent = validUrls[0]?.value || '';
      if (this.summaryCustomLinkAnchor) {
        if (validUrls[0]) this.summaryCustomLinkAnchor.href = validUrls[0].value;
        else this.summaryCustomLinkAnchor.removeAttribute('href');
      }
      if (this.summaryWifiValue) {
        const hidden = this.hiddenInput?.checked ? 'Tak' : 'Nie';
        this.summaryWifiValue.textContent = `${this.ssidInput?.value || ''} · ${hidden} · ${this.encryptionInput?.value || 'None'}`;
      }
    }

    revokePreviewObjectUrl() {
      this.previewToken += 1;
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
      if (this.filePreview) {
        this.filePreview.onload = null;
        this.filePreview.onerror = null;
        this.filePreview.removeAttribute('src');
      }
    }
  }

  customElements.define('uvfeel-personalization', UVFeelPersonalization);
})();
